<template>
    <div
        v-if="adBlockerOfferStore.isOfferVisible && offerStyles"
        :class="[$style.offer, $style[offerStyles.backgroundClass]]"
    >
        <img
            :class="$style.image"
            :src="offerStyles?.srcImg"
            alt="Ad Blocker Offer"
        />
        <Typography
            size="m"
            type="paragraph"
            :class="$style.desc"
            accent
        >
            <span :class="$style.desc_accent">{{ $tr('adBlockerOffer.title') }}</span>
            <br />
            {{ $tr('adBlockerOffer.desc') }}
        </Typography>
        <div :class="$style.buttons">
            <OutlineButton
                color="white-60"
                width="full"
                interfaceTarget="ad-blocker-offer-skip"
                :label="$tr('adBlockerOffer.skip')"
                @click="dismiss"
            />
            <Button
                width="full"
                color="salad"
                interfaceTarget="ad-blocker-offer-buy"
                :label="$tr('adBlockerOffer.activate')"
                @click="onBuy"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import OutlineButton from '@/components/Buttons/OutlineButton.vue'
import Typography from '@/components/Typography.vue'
import type { ChargeResponse, DisableAdOrder } from '@/modules/in-game-payments'
import { useAdBlockerOffer } from '@/store/ad-blocker-store'
import { shuffle } from '@/utils/helpers'

import AdBlockOfferImage1 from './images/ad-block-offer-1.webp'
import AdBlockOfferImage2 from './images/ad-block-offer-2.webp'
import AdBlockOfferImage3 from './images/ad-block-offer-3.webp'

const props = defineProps<{
    openDisabledAdModal: (() => Promise<ChargeResponse<DisableAdOrder> | null>) | undefined
}>()

const adBlockerOfferStore = useAdBlockerOffer()
const offerStyles = ref<{ srcImg: string; backgroundClass: 'bg_yellow' | 'bg_red' | 'bg_green' } | undefined>()

function shuffleStyles() {
    offerStyles.value = shuffle([
        { srcImg: AdBlockOfferImage1, backgroundClass: 'bg_yellow' as const },
        { srcImg: AdBlockOfferImage2, backgroundClass: 'bg_green' as const },
        { srcImg: AdBlockOfferImage3, backgroundClass: 'bg_red' as const },
    ]).slice(0)[0]
}

shuffleStyles()

function dismiss() {
    adBlockerOfferStore.dismissOffer()
    shuffleStyles()
}

async function onBuy() {
    if (props.openDisabledAdModal) {
        try {
            const data = await props.openDisabledAdModal()
            if (data?.data?.status === 'PAID') {
                adBlockerOfferStore.completeOffer()
                shuffleStyles()
            }
        } catch {
            dismiss()
        }
    }
}
</script>

<style module>
.offer {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    background-color: black;
    justify-content: center;
    flex-direction: column;
    gap: 16px;
    padding: 16px 0 70px;
    z-index: 100;
}

.bg_yellow {
    background-image: radial-gradient(107.35% 106.13% at 50% 100%, rgba(255, 200, 80, 0.16) 0%, rgba(0, 0, 0, 0) 100%);
}

.bg_green {
    background-image: radial-gradient(107.35% 106.13% at 50% 100%, rgba(192, 255, 71, 0.16) 0%, rgba(0, 0, 0, 0) 100%);
}

.bg_red {
    background-image: radial-gradient(107.35% 106.13% at 50% 100%, rgba(255, 156, 156, 0.16) 0%, rgba(0, 0, 0, 0) 100%);
}

.image {
    padding: 20px;
    padding-bottom: 0;
    width: clamp(100px, 55vw, 200px);
    aspect-ratio: 1;
    object-fit: contain;
    flex-shrink: 0;
    max-height: 30vh;
}

.desc {
    color: white;
    text-align: center;
    width: 100%;
    width: clamp(100px, 55vw, 280px);
}

.desc_accent {
    color: #ff9c9c;
}

.buttons {
    display: flex;
    gap: 12px;
    width: 100%;
    width: clamp(200px, 55vw, 280px);
}
</style>
